/**
 * 搜索面板 — 三栏布局 + Shopify suggest.json
 */

class SearchPanel {
  constructor() {
    this.cfg = window.searchPanelConfig || {};
    this.panel = document.getElementById('searchPanel');
    this.overlay = document.getElementById('searchPanelOverlay');
    this.input = document.getElementById('searchPanelInput');
    this.clearBtn = document.getElementById('searchPanelClear');
    this.grid = document.getElementById('searchPanelGrid');
    this.loading = document.getElementById('searchLoading');
    this.noResults = document.getElementById('searchNoResults');
    this.footerBar = document.getElementById('searchPanelFooterBar');
    this.viewAllLink = document.getElementById('viewAllResults');

    this.idleLeft = document.getElementById('searchIdleLeft');
    this.idleMiddle = document.getElementById('searchIdleMiddle');
    this.idleProducts = document.getElementById('searchIdleProducts');
    this.liveSuggestions = document.getElementById('searchLiveSuggestions');
    this.suggestionsList = document.getElementById('suggestionsList');
    this.liveMiddle = document.getElementById('searchLiveMiddle');
    this.categoriesList = document.getElementById('categoriesList');
    this.brandsList = document.getElementById('brandsList');
    this.liveProducts = document.getElementById('searchLiveProducts');
    this.productsList = document.getElementById('productsList');

    this.searchTimeout = null;
    this.debounceDelay = 350; // 性能优化：增加防抖延迟，减少无效请求
    this.prefetchTimeout = null;
    this.prefetchDelay = 2000;
    this._searchId = 0;
    this.minChars = 2; // 性能优化：最小字符数改回2，避免单字符无效搜索
    
    // 搜索缓存配置
    this.searchCache = new Map();
    this.searchCacheMaxSize = 20; // 最多缓存20条搜索结果
    this.searchCacheTtl = 5 * 60 * 1000; // 缓存5分钟
    this.historyKey = 'shopify_search_history';
    this.maxHistoryItems = 8;
    this.navCacheKey = 'search_panel_nav_index_v2';
    this.navIndex = [];
    this.idleProductsHtml = this.idleProducts?.innerHTML || '';

    // DEBUG: set to true or add ?searchdebug=1 to URL
    this.DEBUG = new URLSearchParams(window.location.search).get('searchdebug') === '1';
    if (this.DEBUG) {
      console.log('[SearchPanel] window.searchPanelConfig.navigationData:', JSON.stringify(window.searchPanelConfig.navigationData, null, 2));
    }

    // 全量 SKU 索引（用于后缀/子串模糊匹配，弥补 suggest.json 不做 SKU 片段查询的缺陷）
    this.skuIndex = this.loadSkuIndex();

    this.applyStaticStrings();
    this.wrapIdleProducts();
    this.init();
  }

  loadSkuIndex() {
    // 走全量产品 JSON 接口（带 variants.sku），顺序分页拉满全店后一次性固化索引。
    // 自动探测总页数：第 1 页用 limit=250 拉取，用 total_price/首尾页探测，避免写死页数导致漏产品。
    const PER = 250;
    this.skuIndex = [];
    this._skuIndexLoaded = false;
    this._skuIndexReady = new Promise((resolve) => {
      this._resolveSkuIndex = resolve;
    });

    let page = 1;
    const collect = () =>
      fetch(`/collections/all/products.json?limit=${PER}&page=${page}`)
        .then((r) => r.json())
        .then((data) => {
          const products = (data && data.products) || [];
          products.forEach((p) => {
            (p.variants || []).forEach((v) => {
              if (v.sku) {
                this.skuIndex.push({
                  sku: v.sku,
                  id: p.id,
                  title: p.title,
                  vendor: p.vendor,
                  type: p.type,
                  price: p.price,
                  url: p.url,
                  img: (p.images && p.images[0]) ? (p.images[0].src || '') : '',
                  _skuNorm: String(v.sku).toLowerCase().replace(/\s+/g, '')
                });
              }
            });
          });
          page++;
          // 本页满 PER 才说明可能还有下一页，否则到底（自动探测，不写死页数）
          if (products.length === PER) return collect();
          this._skuIndexLoaded = true;
          this._resolveSkuIndex();
          if (this.DEBUG) console.log('[SearchPanel] SKU index loaded:', this.skuIndex.length, 'variants across', page - 1, 'pages');
        })
        .catch(() => {
          // 单页失败不中断，继续下一页直到到底
          page++;
          if (page <= 20) return collect(); // 硬上限 20 页（5000 产品）防死循环
          this._skuIndexLoaded = true;
          this._resolveSkuIndex();
        });
    collect();
    return this.skuIndex;
  }

  // 等待 SKU 索引全量加载完成（自动探测的 5 页都拉完）
  async waitSkuIndex() {
    if (this._skuIndexLoaded) return this.skuIndex;
    await this._skuIndexReady;
    return this.skuIndex;
  }

  // SKU 后缀/子串匹配：搜索词若为某 SKU 的后缀或子串，则召回该 SKU 的产品
  matchSkuSuffix(query) {
    // 索引未全量加载完成前不用半截数据，保证多次搜索结果一致
    if (!this._skuIndexLoaded || !this.skuIndex.length) return [];
    const q = String(query || '').toLowerCase().replace(/\s+/g, '');
    if (q.length < 2) return [];

    const hits = this.skuIndex.filter((item) => item._skuNorm.includes(q));

    // 评分：完全相等 > 后缀（ends with）> 子串
    const scored = hits.map((item) => {
      let score = 0;
      if (item._skuNorm === q) score = 3;
      else if (item._skuNorm.endsWith(q)) score = 2;
      else score = 1;
      return { ...item, _skuScore: score };
    });
    scored.sort((a, b) => b._skuScore - a._skuScore || (a.sku || '').localeCompare(b.sku || ''));
    return scored;
  }

  applyStaticStrings() {
    const s = this.cfg.strings || {};
    const setText = (id, text) => {
      const el = document.getElementById(id);
      if (el && text) el.textContent = text;
    };
    setText('searchSuggestionsHeading', s.searchSuggestions);
    setText('hotSearchHeading', s.hotSearch);
    setText('recentHeading', s.recent);
    setText('collectionsHeading', s.collections);
    const clearBtn = document.getElementById('clearHistory');
    if (clearBtn && s.clearHistory) clearBtn.textContent = s.clearHistory;
    const noTitle = document.getElementById('noResultsTitle');
    const noHint = document.getElementById('noResultsHint');
    if (noTitle && s.noResults) noTitle.textContent = s.noResults;
    if (noHint && s.tryDifferent) noHint.textContent = s.tryDifferent;
  }

  wrapIdleProducts() {
    const container = this.idleProducts;
    if (!container) return;
    const title = container.querySelector('.search-col-title');
    const stack = container.querySelector('.search-product-stack');
    if (!stack || container.querySelector('.search-product-scroll')) return;

    const scrollDiv = document.createElement('div');
    scrollDiv.className = 'search-product-scroll';
    scrollDiv.appendChild(stack.cloneNode(true));
    container.innerHTML = '';
    if (title) container.appendChild(title.cloneNode(true));
    container.appendChild(scrollDiv);
  }

  getSuggestEndpoint() {
    const base = this.cfg.suggestUrl || window.routes?.predictive_search_url || '/search/suggest';
    return base.includes('.json') ? base : `${base.replace(/\/?$/, '')}.json`;
  }

  getSearchUrl() {
    return this.cfg.searchUrl || '/search';
  }

  init() {
    this.bootstrapNavigationIndex();
    this.bindEvents();
    this.loadHistory();
  }

  bootstrapNavigationIndex() {
    const navData = this.cfg.navigationData;
    if (navData && Object.keys(navData).length > 0) {
      this.navIndex = this.buildNavIndexFromConfig();
      if (this.DEBUG) {
        console.log('[SearchPanel] navIndex built from navigationData:', JSON.stringify(this.navIndex, null, 2));
        console.log('[SearchPanel] navData keys:', Object.keys(navData));
        const v = navData['vanities & storage'];
        console.log('[SearchPanel] navData["vanities & storage"]:', JSON.stringify(v, null, 2));
      }
      return;
    }
    const cached = this.loadNavIndexFromCache();
    if (cached.length) {
      this.navIndex = cached;
      if (this.DEBUG) {
        console.log('[SearchPanel] navIndex loaded from cache:', JSON.stringify(this.navIndex, null, 2));
      }
      return;
    }
    const parsed = this.parseNavigationFromDom();
    if (parsed.length) {
      this.navIndex = parsed;
      this.saveNavIndexToCache(parsed);
      if (this.DEBUG) {
        console.log('[SearchPanel] navIndex built from DOM:', JSON.stringify(this.navIndex, null, 2));
      }
    }
  }

  loadNavIndexFromCache() {
    try {
      const raw = localStorage.getItem(this.navCacheKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  saveNavIndexToCache(index) {
    try {
      localStorage.setItem(this.navCacheKey, JSON.stringify(index));
    } catch (e) {
      // ignore storage errors
    }
  }

  buildNavIndexFromConfig() {
    const navData = this.cfg.navigationData || {};
    return Object.values(navData)
      .filter((group) => Array.isArray(group?.children) && group.children.length > 0)
      .map((group) => ({
        title: group.title || '',
        url: group.url || '',
        items: group.children.map((child) => ({
          title: child.title || '',
          url: child.url || '',
          children: Array.isArray(child.children) ? child.children.map((sub) => ({
            title: this.normalizeChildTitle(sub.title || '', child.title || '', group.title || ''),
            url: sub.url || ''
          })) : []
        }))
      }));
  }

  normalizeChildTitle(subTitle, childTitle, groupTitle) {
    const sub = subTitle.toLowerCase();
    const child = childTitle.toLowerCase();

    if (sub.includes(child)) return subTitle;

    if (child === 'baths') {
      if (sub.includes('bathtubs')) return subTitle;
      if (/\bbath\b/i.test(subTitle)) return subTitle.replace(/\bbath\b/i, 'Bathtub');
      return `${subTitle} Bathtubs`;
    }

    return `${subTitle} ${child}`;
  }

  parseNavigationFromDom() {
    return [];
  }

  bindEvents() {
    document.querySelectorAll('#header-search, .search-header a').forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    this.panel?.querySelector('.search-panel-close')?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());

    this.input?.addEventListener('input', (e) => this.handleInput(e));
    this.input?.addEventListener('keydown', (e) => this.handleKeydown(e));
    this.clearBtn?.addEventListener('click', () => this.clearInput());

    document.getElementById('clearHistory')?.addEventListener('click', () => this.clearHistory());

    document.querySelectorAll('#searchTrending .search-link').forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const q = link.textContent.trim();
        if (this.input) {
          this.input.value = q;
          this.toggleClearButton();
          this.handleInput({ target: this.input });
        }
      });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.panel?.classList.contains('active')) this.close();
    });
  }

  open() {
    if (!this.panel) return;

    // 预热：面板打开时确保 SKU 索引全量拉取在进行/完成，
    // 首次输入即可匹配到全量 SKU（避免等待 5 页 products.json 造成搜索空白）
    if (!this._skuIndexLoaded) {
      this.waitSkuIndex(); // 让后台拉取继续，不阻塞 UI
    }

    // Always capture current scroll position before locking
    const currentScrollY = window.scrollY;
    const currentScrollX = window.scrollX;
    
    // If already locked (from a previous open), restore first to get fresh position
    if (document.body.style.position === 'fixed') {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.documentElement.style.position = '';
      document.documentElement.style.top = '';
      document.documentElement.style.width = '';
      window.scrollTo(currentScrollX, currentScrollY);
    }

    this._scrollY = currentScrollY;
    this._scrollX = currentScrollX;

    this.panel.classList.add('active');
    this.overlay?.classList.add('active');
    if (this.overlay) this.overlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('search-panel-open');

    // Remove no-scroll class if present (from mobile menu, cart, etc.)
    // The search panel uses position:fixed for its own scroll lock
    document.body.classList.remove('no-scroll');

    // Lock scroll using position: fixed
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this._scrollY}px`;
    document.body.style.width = '100%';
    document.documentElement.style.position = 'fixed';
    document.documentElement.style.top = `-${this._scrollY}px`;
    document.documentElement.style.width = '100%';

    setTimeout(() => this.input?.focus(), 50);
  }

  close() {
    if (!this.panel) return;
    this.panel.classList.remove('active');
    this.overlay?.classList.remove('active');
    if (this.overlay) this.overlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('search-panel-open');

    // Restore scroll position and remove fixed positioning
    const scrollY = this._scrollY || 0;
    const scrollX = this._scrollX || 0;

    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.documentElement.style.position = '';
    document.documentElement.style.top = '';
    document.documentElement.style.width = '';

    // Always clear no-scroll on close (let other components add it back if needed)
    document.body.classList.remove('no-scroll');

    // Restore scroll position after a tiny delay to ensure DOM is ready
    requestAnimationFrame(() => {
      window.scrollTo(scrollX, scrollY);
    });

    this.resetPanel();
  }

  clearInput() {
    if (this.input) this.input.value = '';
    this.toggleClearButton();
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    this.clearPrefetchProducts();
    this.resetIdleProducts();
    this.showIdleState();
    this.hideLoading();
    this.noResults?.classList.add('hidden');
    this.footerBar?.classList.add('hidden');
  }

  resetPanel() {
    this.clearInput();
  }

  toggleClearButton() {
    const has = this.input?.value?.length > 0;
    this.clearBtn?.classList.toggle('hidden', !has);
  }

  handleInput(e) {
    const query = e.target.value.trim();
    this.toggleClearButton();

    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    if (this.prefetchTimeout) clearTimeout(this.prefetchTimeout);

    if (query.length < this.minChars) {
      this.clearPrefetchProducts();
      this.showIdleState();
      this.hideLoading();
      this.noResults?.classList.add('hidden');
      this.footerBar?.classList.add('hidden');
      return;
    }

    // 280ms debounce for quick search
    this.searchTimeout = setTimeout(() => this.performSearch(query), this.debounceDelay);

    // 2s delay for silent product prefetch (show in idle products column)
    this.prefetchTimeout = setTimeout(() => {
      this.prefetchProducts(query);
    }, this.prefetchDelay);
  }

  handleKeydown(e) {
    if (e.key !== 'Enter') return;
    const query = this.input?.value?.trim();
    if (!query) return;
    e.preventDefault();
    this.addToHistory(query);
    this.input?.form?.submit();
  }

  showIdleState() {
    this.idleLeft?.classList.remove('hidden');
    this.idleMiddle?.classList.remove('hidden');
    this.idleProducts?.classList.remove('hidden');
    this.liveSuggestions?.classList.add('hidden');
    this.liveMiddle?.classList.add('hidden');
    this.liveProducts?.classList.add('hidden');
    this.grid?.classList.remove('hidden');
  }

  showLiveLayout() {
    this.idleLeft?.classList.add('hidden');
    this.idleMiddle?.classList.add('hidden');
    this.idleProducts?.classList.add('hidden');
    this.liveSuggestions?.classList.remove('hidden');
    this.liveMiddle?.classList.remove('hidden');
    this.liveProducts?.classList.remove('hidden');
    this.grid?.classList.remove('hidden');
  }

  // 搜索词变体生成（用于增强搜索）
  generateSearchVariants(query) {
    const variants = [query];
    const q = query.trim();
    
    // 移除常见停用词（如 "the", "a", "an" 等，根据需要扩展）
    const stopWords = ['the', 'a', 'an', 'and', 'or', 'for', 'with'];
    const words = q.split(' ').filter(w => w.length > 1 && !stopWords.includes(w.toLowerCase()));
    
    // 生成词的变体组合
    if (words.length > 1) {
      for (let i = 0; i < words.length; i++) {
        // 每次移除一个词
        const withoutOne = [...words];
        withoutOne.splice(i, 1);
        if (withoutOne.length > 0) {
          variants.push(withoutOne.join(' '));
        }
      }
    }
    
    return variants.slice(0, 3); // 限制变体数量，避免过多请求
  }

  // 清空搜索缓存
  clearSearchCache() {
    this.searchCache.clear();
    if (this.DEBUG) {
      console.log('[SearchPanel] Search cache cleared');
    }
  }

  async performSearch(query) {
    if (this.DEBUG) {
      console.log('[SearchPanel] performSearch called with:', query);
    }
    this.clearPrefetchProducts();
    this.footerBar?.classList.add('hidden');
    this.showLiveLayout();
    this.noResults?.classList.add('hidden');
    this.loading?.classList.remove('hidden');

    // Increment search ID to cancel any in-flight search
    const searchId = ++this._searchId;

    try {
      // 恢复双重 API 调用，以获取更多的产品结果
      const [suggestData, productsData] = await Promise.all([
        this.fetchSuggest(query),
        this.fetchProductsOnly(query)
      ]);
      
      // Ignore stale results
      if (searchId !== this._searchId) return;
      
      // 合并产品并去重，但不做复杂排序
      if (suggestData?.resources?.results?.products && productsData?.length > 0) {
        const allProducts = [
          ...suggestData.resources.results.products,
          ...productsData
        ];
        const seen = new Set();
        const uniqueProducts = allProducts.filter(p => {
          const id = p.id || p.url;
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
        });
        suggestData.resources.results.products = uniqueProducts;
        
        if (this.DEBUG) {
          console.log('[SearchPanel] Total products after merge:', uniqueProducts.length);
        }
      }
      
      // SKU 子串匹配兜底：始终与 API 结果合并去重，让「2154」能召回全部含 2154 的 SKU（不只前缀能命中的那几个）
      // 等待 SKU 索引全量加载完成，避免首次搜索时索引未就绪导致空白
      await this.waitSkuIndex();
      const apiProducts = suggestData?.resources?.results?.products || [];
      const skuHits = this.matchSkuSuffix(query);
      if (skuHits.length > 0) {
        const skuProducts = skuHits.map((s) => ({
          title: s.title,
          vendor: s.vendor,
          product_type: s.type,
          url: s.url,
          id: s.id,
          variants: [{ sku: s.sku }],
          price_min: s.price,
          featured_image: { url: s.img },
          _fromSkuIndex: true
        }));
        // 合并：API 优先，SKU 兜底补全，按 id 去重
        const merged = [...apiProducts, ...skuProducts];
        const seen = new Set();
        const unique = merged.filter((p) => {
          const id = p.id || p.url;
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
        });
        suggestData.resources.results.products = unique;
        suggestData._skuFallback = skuHits.length > 0;
        if (this.DEBUG) {
          console.log('[SearchPanel] SKU merge:', apiProducts.length, '+', skuHits.length, '=', unique.length);
        }
      }

      this.renderResults(suggestData, query);
    } catch (err) {
      console.error('Search panel:', err);
      this.hideLoading();
      this.noResults?.classList.remove('hidden');
      this.updateFooter(query);
    }
  }

  async prefetchProducts(query) {
    // Silent fetch: only products, limit 15, no loading indicator
    // Cancel if a full search has already started
    if (this.searchTimeout) return;

    try {
      const products = await this.fetchProductsOnly(query);
      if (products.length > 0) {
        this.renderPrefetchProducts(this.sortProductsByRelevance(products, query));
      }
    } catch (err) {
      // Silently fail
    }
  }

  async fetchProductsOnly(query) {
    const endpoint = this.getSuggestEndpoint();
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product',
      'resources[limit]': '50',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,vendor,product_type,variants.sku',
    });

    try {
      const res = await fetch(`${endpoint}?${params.toString()}`);
      if (!res.ok) return [];
      const data = await res.json();
      return data?.resources?.results?.products || [];
    } catch (err) {
      console.error('[SearchPanel] fetchProductsOnly failed:', err);
      return [];
    }
  }

  // 模糊匹配评分函数
  fuzzyMatchScore(query, text) {
    if (!text) return 0;
    const q = this.normalizeTerm(query);
    const t = this.normalizeTerm(text);
    if (!q || !t) return 0;

    // 完全匹配（最高优先级）
    if (q === t) return 1000;
    
    // 前缀匹配
    if (t.startsWith(q)) return 900;
    
    // 包含匹配
    if (t.includes(q)) return 800;
    
    // 每个词都匹配
    const qTokens = q.split(' ');
    const tTokens = t.split(' ');
    let matches = 0;
    qTokens.forEach(qt => {
      if (tTokens.some(tt => tt.includes(qt) || qt.includes(tt))) {
        matches++;
      }
    });
    if (matches === qTokens.length) return 700 + matches * 10;
    
    // 部分匹配
    return qTokens.filter(qt => t.includes(qt)).length * 50;
  }

  // 增强的产品智能排序
  sortProductsByRelevance(products, query) {
    if (!products || products.length === 0) return [];

    const qNorm = this.normalizeTerm(query);
    const qTokens = qNorm.split(' ').filter((t) => t.length >= 2);

    const scoredProducts = products.map(p => {
      // 计算综合评分
      let score = 0;
      const searchText = [
        p.title,
        p.vendor,
        p.product_type,
        p.variants?.[0]?.sku,
        (p.tags || []).join(' ')
      ].filter(Boolean).join(' ');

      score += this.fuzzyMatchScore(query, searchText);
      score += this.fuzzyMatchScore(query, p.title) * 2; // 标题权重加倍
      score += this.fuzzyMatchScore(query, p.variants?.[0]?.sku) * 1.5; // SKU 权重较高

      // SKU 片段加权：搜索词命中 SKU（子串/后缀）时大幅提权
      // 例如搜「2154」可让 SKU「SS2154」排最前
      const skuNorm = (p.variants?.[0]?.sku || '').toLowerCase();
      if (skuNorm) {
        for (const tok of qTokens) {
          if (skuNorm.indexOf(tok) !== -1) {
            score += 1500;
            break;
          }
        }
      }

      return { ...p, _score: score };
    });
    
    // 先按评分降序排列
    scoredProducts.sort((a, b) => b._score - a._score);
    
    // 检查是否有任何产品有评分
    const hasScoredProducts = scoredProducts.some(p => p._score > 0);
    
    if (hasScoredProducts) {
      // 有评分产品时，只过滤掉完全无评分的产品
      return scoredProducts.filter(p => p._score > 0);
    } else {
      // 如果没有产品匹配成功，返回原始产品（保持API返回的顺序）
      if (this.DEBUG) {
        console.log('[SearchPanel] No products matched fuzzy search, returning all results');
      }
      return products;
    }
  }

  renderPrefetchProducts(products) {
    const container = document.getElementById('searchIdleProducts');
    if (!container) return;

    const title = container.querySelector('.search-col-title');
    const html = this.renderProducts(products);

    container.innerHTML = '';
    if (title) container.appendChild(title.cloneNode(true));
    const scrollDiv = document.createElement('div');
    scrollDiv.className = 'search-product-scroll';
    scrollDiv.innerHTML = `<div class="search-product-stack">${html}</div>`;
    container.appendChild(scrollDiv);
  }

  clearPrefetchProducts() {
    if (this.prefetchTimeout) {
      clearTimeout(this.prefetchTimeout);
      this.prefetchTimeout = null;
    }
  }

  resetIdleProducts() {
    if (this.idleProducts && this.idleProductsHtml) {
      const title = this.idleProducts.querySelector('.search-col-title');
      const originalStack = this.idleProducts.querySelector('.search-product-stack');

      this.idleProducts.innerHTML = this.idleProductsHtml;

      if (originalStack) {
        const stackClone = originalStack.cloneNode(true);
        this.idleProducts.innerHTML = '';
        if (title) this.idleProducts.appendChild(title.cloneNode(true));
        const scrollDiv = document.createElement('div');
        scrollDiv.className = 'search-product-scroll';
        scrollDiv.appendChild(stackClone);
        this.idleProducts.appendChild(scrollDiv);
      }
    }
  }

  async fetchSuggest(query) {
    const endpoint = this.getSuggestEndpoint();
    // 使用稳定的 Shopify API 参数
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'query,product,collection',
      'resources[limit]': '50', // 性能优化：减少返回结果数量
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,vendor,product_type,variants.sku',
    });

    if (this.DEBUG) {
      console.log('[SearchPanel] fetchSuggest URL:', `${endpoint}?${params.toString()}`);
    }

    try {
      const res = await fetch(`${endpoint}?${params.toString()}`);
      if (!res.ok) throw new Error('suggest failed');
      const data = await res.json();
      
      if (this.DEBUG) {
        console.log('[SearchPanel] fetchSuggest raw response:', data);
        console.log('[SearchPanel] products from API:', data?.resources?.results?.products?.length || 0);
      }
      
      return data;
    } catch (err) {
      console.error('[SearchPanel] fetchSuggest failed:', err);
      // 返回一个空结果结构，防止后续代码出错
      return {
        resources: {
          results: {
            products: [],
            collections: [],
            queries: []
          }
        }
      };
    }
  }

  // 搜索缓存：获取
  getSearchCache(query) {
    const key = query.toLowerCase().trim();
    const cached = this.searchCache.get(key);
    if (!cached) return null;
    
    // 检查是否过期
    if (Date.now() - cached.timestamp > this.searchCacheTtl) {
      this.searchCache.delete(key);
      return null;
    }
    
    return cached.data;
  }

  // 搜索缓存：设置
  setSearchCache(query, data) {
    const key = query.toLowerCase().trim();
    
    // 如果缓存已满，删除最旧的条目
    if (this.searchCache.size >= this.searchCacheMaxSize) {
      const firstKey = this.searchCache.keys().next().value;
      this.searchCache.delete(firstKey);
    }
    
    this.searchCache.set(key, {
      data: data,
      timestamp: Date.now()
    });
    
    if (this.DEBUG) {
      console.log('[SearchPanel] Search cached:', key);
    }
  }

  renderResults(data, query) {
    this.hideLoading();

    const results = data?.resources?.results || {};
    const queries = results.queries || [];
    const products = results.products || [];
    const collections = results.collections || [];

    if (this.DEBUG) {
      console.log('[SearchPanel] renderResults - products count:', products.length, products);
      console.log('[SearchPanel] renderResults - collections count:', collections.length);
      console.log('[SearchPanel] renderResults - vendors count:', this.uniqueVendors(products).length);
    }

    const vendors = this.uniqueVendors(products);

    const hasAny =
      queries.length + products.length + collections.length + vendors.length > 0;

    if (!hasAny) {
      this.grid?.classList.add('hidden');
      this.noResults?.classList.remove('hidden');
      this.updateFooter(query);
      return;
    }

    this.grid?.classList.remove('hidden');
    this.noResults?.classList.add('hidden');

    if (this.suggestionsList) this.suggestionsList.innerHTML = this.renderQueries(queries, query);
    if (this.categoriesList) this.categoriesList.innerHTML = this.renderCollections(collections, query);
    if (this.brandsList) this.brandsList.innerHTML = this.renderBrands(vendors);
    if (this.productsList) this.productsList.innerHTML = this.renderProducts(this.sortProductsByRelevance(products, query));

    this.bindResultClicks();
    this.updateFooter(query);
  }

  attrSafe(val) {
    return String(val ?? '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;');
  }

  renderQueries(queries, fallbackQuery) {
    if (!queries.length) {
      const url = `${this.getSearchUrl()}?q=${encodeURIComponent(fallbackQuery)}`;
      return `<li><a class="search-link" href="${this.attrSafe(url)}">${this.escapeHtml(fallbackQuery)}</a></li>`;
    }
    return queries
      .map((q) => {
        const text = q.text || q.styled_text || '';
        const href = q.url || `${this.getSearchUrl()}?q=${encodeURIComponent(text)}`;
        return `<li><a class="search-link" href="${this.attrSafe(href)}">${this.escapeHtml(text)}</a></li>`;
      })
      .join('');
  }

  renderCollections(collections, query) {
    const keywordMap = this.buildKeywordMap(query);
    if (keywordMap.length > 0) {
      return keywordMap.map((item) => {
        const url = item.url || `${this.getSearchUrl()}?q=${encodeURIComponent(item.urlParam || item.title)}`;
        return `<li><a class="search-link" href="${this.attrSafe(url)}">${this.escapeHtml(item.title)}</a></li>`;
      }).join('');
    }
    if (!collections.length) {
      return `<li class="search-link-list__empty">—</li>`;
    }
    return collections
      .map((c) => {
        const title = c.title || '';
        const url = c.url || '#';
        return `<li><a class="search-link" href="${this.attrSafe(url)}">${this.escapeHtml(title)}</a></li>`;
      })
      .join('');
  }

  buildKeywordMap(query) {
    const q = query || '';
    if (this.DEBUG) {
      console.log('[SearchPanel] buildKeywordMap query="' + q + '", navIndex=', JSON.stringify(this.navIndex, null, 2));
    }
    const match = this.findBestNavGroupMatch(q);
    if (match) {
      // Prefer children (2nd-level sub-items), fallback to items (group root direct children)
      const subItems = (match.item.children && match.item.children.length > 0)
        ? match.item.children
        : (match.item.items || []);

      if (subItems.length) {
        if (this.DEBUG) {
          console.log('[SearchPanel] matched item "' + match.item.title + '" → sub-items:', subItems);
        }
        return subItems.map((sub) => ({
          title: sub.title,
          url: sub.url || `${this.getSearchUrl()}?q=${encodeURIComponent(sub.title)}`
        }));
      }

      // Sub-item matched but has no children → walk back up to its group's items
      if (match.item.children !== undefined && !(match.item.children && match.item.children.length > 0)) {
        const parentGroup = this.navIndex.find((g) => g.title === match.groupTitle);
        const parentItems = (parentGroup && parentGroup.items) ? parentGroup.items : [];
        if (parentItems.length) {
          if (this.DEBUG) {
            console.log('[SearchPanel] matched sub-item "' + match.item.title + '" with no children, fallback to group "' + match.groupTitle + '" items:', parentItems);
          }
          return parentItems.map((sub) => ({
            title: sub.title,
            url: sub.url || `${this.getSearchUrl()}?q=${encodeURIComponent(sub.title)}`
          }));
        }
      }

      if (this.DEBUG) {
        console.log('[SearchPanel] matched item "' + match.item.title + '" but has no sub-items, fallback to item itself');
      }
      return [{
        title: match.item.title,
        url: match.item.url || `${this.getSearchUrl()}?q=${encodeURIComponent(match.item.title)}`
      }];
    }
    if (this.DEBUG) {
      console.log('[SearchPanel] no nav match for "' + q + '"');
    }
    return [];
  }

  findBestNavGroupMatch(query) {
    if (!this.navIndex.length) return null;
    const qNorm = this.normalizeTerm(query);
    if (!qNorm) return null;

    let best = null;
    let bestScore = 0;
    let bestLevel = 0;

    this.navIndex.forEach((group) => {
      const groupNorm = this.normalizeTerm(group.title);
      const groupSing = this.singularTokenMatch(qNorm, groupNorm);
      const groupScore = this.calcMatchScore(qNorm, groupNorm, groupSing);
      const groupLevel = this.matchLevel(qNorm, groupNorm, groupSing);

      if (groupScore > bestScore || (groupScore === bestScore && groupLevel > bestLevel)) {
        bestScore = groupScore;
        bestLevel = groupLevel;
        best = { groupTitle: group.title, item: group };
      }

      group.items.forEach((item) => {
        const itemNorm = this.normalizeTerm(item.title);
        if (!itemNorm) return;
        const itemSing = this.singularTokenMatch(qNorm, itemNorm);
        const itemScore = this.calcMatchScore(qNorm, itemNorm, itemSing);
        const itemLevel = this.matchLevel(qNorm, itemNorm, itemSing);

        if (itemScore > bestScore || (itemScore === bestScore && itemLevel > bestLevel)) {
          bestScore = itemScore;
          bestLevel = itemLevel;
          best = { groupTitle: group.title, item };
        }
      });
    });

    if (this.DEBUG) {
      console.log('[SearchPanel] findBestNavMatch for "' + query + '": best=' + JSON.stringify(best) + ', score=' + bestScore + ', level=' + bestLevel);
    }

    return bestScore >= 80 ? best : null;
  }

  normalizeTerm(text) {
    return String(text || '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  singularizeToken(token) {
    if (token.endsWith('ies') && token.length > 3) return `${token.slice(0, -3)}y`;
    if (token.endsWith('es') && token.length > 3) return token.slice(0, -2);
    if (token.endsWith('s') && token.length > 3) return token.slice(0, -1);
    return token;
  }

  toSearchTokens(text) {
    const normalized = this.normalizeTerm(text);
    if (!normalized) return [];
    const tokens = normalized.split(' ').filter(Boolean);
    const expanded = new Set();
    tokens.forEach((token) => {
      expanded.add(token);
      expanded.add(this.singularizeToken(token));
    });
    return Array.from(expanded).filter(Boolean);
  }

  singularTokenMatch(qNorm, itemNorm) {
    if (!qNorm || !itemNorm) return 0;
    const qTokens = qNorm.split(' ').filter(Boolean);
    const itemTokens = itemNorm.split(' ').filter(Boolean);
    for (const qt of qTokens) {
      const qtSing = this.singularizeToken(qt);
      for (const it of itemTokens) {
        const itSing = this.singularizeToken(it);
        // Exact singular match at token level: "bathtubs" → "bathtub"
        if (qtSing === itSing) return 2;
        // Prefix singular match: "bathtubs" starts with "bathtub"
        if (qtSing.length >= 4 && itSing.length >= 4 && (qtSing.startsWith(itSing) || itSing.startsWith(qtSing))) return 1;
      }
    }
    return 0;
  }

  singularSubstringMatch(qNorm, itemNorm) {
    if (!qNorm || !itemNorm) return false;
    if (itemNorm.includes(qNorm) || qNorm.includes(itemNorm)) return true;
    const qSing = this.singularizeToken(qNorm);
    const itSing = this.singularizeToken(itemNorm);
    if (itSing.includes(qSing) || qSing.includes(itSing)) return true;
    return false;
  }

  matchLevel(qNorm, norm, singMatch) {
    // Higher = better match type. Used to break ties at same score.
    // 5: exact singular (same word stem, e.g. "bathtubs" → "bathtub")
    // 4: exact string
    // 3: singular substring
    // 2: singular prefix (same 4-char stem)
    // 1: approximate
    if (singMatch === 2) return 5;
    if (qNorm === norm) return 4;
    if (this.singularSubstringMatch(qNorm, norm)) return 3;
    if (singMatch === 1) return 2;
    return 1;
  }

  calcMatchScore(qNorm, norm, singMatch) {
    if (singMatch === 2) return 95; // Exact singular: same word stem, highest priority
    if (qNorm === norm) return 100;
    if (singMatch === 1 && this.singularSubstringMatch(qNorm, norm)) return 90; // Singular + substring
    if (singMatch === 1) return 88; // Singular prefix only
    if (this.singularSubstringMatch(qNorm, norm)) return 85; // Substring without singular
    if (this.isApproxMatch(qNorm, norm)) return 80;
    return 0;
  }

  isApproxMatch(query, candidate) {
    const queryNorm = this.normalizeTerm(query);
    const candidateNorm = this.normalizeTerm(candidate);
    if (!queryNorm || !candidateNorm) return false;
    if (queryNorm.includes(candidateNorm) || candidateNorm.includes(queryNorm)) return true;

    const queryTokens = this.toSearchTokens(queryNorm);
    const candidateTokens = this.toSearchTokens(candidateNorm);

    return queryTokens.some((qt) =>
      candidateTokens.some((ct) => {
        if (qt === ct) return true;
        if (qt.length >= 5 && ct.length >= 5 && qt.slice(0, 5) === ct.slice(0, 5)) return true;
        return false;
      })
    );
  }

  uniqueVendors(products) {
    const seen = new Set();
    const out = [];
    products.forEach((p) => {
      const v = (p.vendor || '').trim();
      if (v && !seen.has(v.toLowerCase())) {
        seen.add(v.toLowerCase());
        out.push(v);
      }
    });
    return out.slice(0, 8);
  }

  renderBrands(vendors) {
    if (!vendors.length) {
      return `<li class="search-link-list__empty">—</li>`;
    }
    return vendors
      .map((v) => {
        const url = `${this.getSearchUrl()}?q=${encodeURIComponent(v)}`;
        return `<li><a class="search-link" href="${this.attrSafe(url)}">${this.escapeHtml(v)}</a></li>`;
      })
      .join('');
  }

  renderProducts(products) {
    if (!products.length) {
      return `<p class="search-product-stack__empty">—</p>`;
    }
    const partLabel = this.cfg.strings?.partNo || 'Part No';
    return products
      .map((p) => {
        const title = p.title || '';
        const url = p.url || '#';
        const img =
          p.featured_image?.url ||
          p.image ||
          'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png';
        const sku = p.variants?.[0]?.sku || p.sku || '';
        const priceHtml = this.formatProductMoney(p);

        const skuLine = sku
          ? `<span class="search-proto-product__sku">${this.escapeHtml(partLabel)}: ${this.escapeHtml(sku)}</span>`
          : '';

        return `
          <a href="${this.attrSafe(url)}" class="search-proto-product">
            <div class="search-proto-product__media">
              <img src="${this.attrSafe(img)}" alt="" loading="lazy" width="72" height="72">
            </div>
            <div class="search-proto-product__body">
              <span class="search-proto-product__title">${this.escapeHtml(title)}</span>
              ${skuLine}
              <span class="search-proto-product__price search-proto-product__price--live">${priceHtml}</span>
            </div>
          </a>
        `;
      })
      .join('');
  }

  formatProductMoney(product) {
    const raw = product.price_min ?? product.price;
    if (raw == null || raw === '') return '';
    const fmt = this.cfg.moneyFormat || window.Shopify?.money_format;
    let cents = null;
    if (typeof raw === 'number' && Number.isFinite(raw)) {
      cents = raw;
    } else if (typeof raw === 'string' && raw.includes('.')) {
      const f = parseFloat(raw);
      if (!Number.isNaN(f)) cents = Math.round(f * 100);
    } else {
      const n = parseInt(raw, 10);
      if (!Number.isNaN(n)) cents = n;
    }
    if (cents == null) return this.escapeHtml(String(raw));
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney && fmt) {
      return Shopify.formatMoney(cents, fmt);
    }
    return this.escapeHtml(String(raw));
  }

  updateFooter(query) {
    if (!this.viewAllLink || !this.footerBar) return;
    const label = this.cfg.strings?.seeAllFor || 'See all results for';
    this.viewAllLink.textContent = `${label} '${query}'`;
    this.viewAllLink.href = `${this.getSearchUrl()}?q=${encodeURIComponent(query)}`;
    this.footerBar.classList.remove('hidden');
  }

  bindResultClicks() {
    const q = this.input?.value?.trim();
    if (!q) return;
    this.panel
      ?.querySelectorAll(
        '#suggestionsList a, #categoriesList a, #brandsList a, #productsList a.search-proto-product'
      )
      .forEach((el) => {
        el.addEventListener('click', () => this.addToHistory(q));
      });
  }

  hideLoading() {
    this.loading?.classList.add('hidden');
  }

  escapeHtml(str) {
    if (str == null) return '';
    const d = document.createElement('div');
    d.textContent = String(str);
    return d.innerHTML;
  }

  loadHistory() {
    try {
      const history = JSON.parse(localStorage.getItem(this.historyKey) || '[]');
      this.renderHistory(history);
    } catch (e) {
      console.error(e);
    }
  }

  renderHistory(history) {
    const listEl = document.getElementById('historyList');
    const clearBtn = document.getElementById('clearHistory');
    if (!listEl) return;

    if (!history.length) {
      listEl.innerHTML = '';
      clearBtn?.classList.add('hidden');
      return;
    }

    clearBtn?.classList.remove('hidden');
    const base = this.getSearchUrl();
    listEl.innerHTML = history
      .map((item) => {
        const enc = encodeURIComponent(item);
        return `
      <li class="history-list__item">
        <a href="${this.attrSafe(`${base}?q=${enc}`)}" class="search-link">${this.escapeHtml(item)}</a>
        <button type="button" class="history-remove" data-query="${this.attrSafe(enc)}" aria-label="Remove">×</button>
      </li>`;
      })
      .join('');

    listEl.querySelectorAll('.history-remove').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const enc = btn.getAttribute('data-query') || '';
        this.removeFromHistory(decodeURIComponent(enc));
      });
    });
  }

  addToHistory(query) {
    if (!query || query.length < 2) return;
    try {
      let history = JSON.parse(localStorage.getItem(this.historyKey) || '[]');
      history = history.filter((item) => item.toLowerCase() !== query.toLowerCase());
      history.unshift(query);
      history = history.slice(0, this.maxHistoryItems);
      localStorage.setItem(this.historyKey, JSON.stringify(history));
      this.renderHistory(history);
    } catch (e) {
      console.error(e);
    }
  }

  removeFromHistory(query) {
    try {
      let history = JSON.parse(localStorage.getItem(this.historyKey) || '[]');
      history = history.filter((item) => item !== query);
      localStorage.setItem(this.historyKey, JSON.stringify(history));
      this.renderHistory(history);
    } catch (e) {
      console.error(e);
    }
  }

  clearHistory() {
    localStorage.removeItem(this.historyKey);
    this.renderHistory([]);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.searchPanel = new SearchPanel();
});
